import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTemplate } from '../components/templates/useTemplate';
import { InlineRename } from '../components/templates/InlineRename';
import { RowMenu } from '../components/templates/RowMenu';
import { ConfirmModal } from '../components/templates/ConfirmModal';
import { AutosaveIndicator } from '../components/templates/AutosaveIndicator';
import { SortableList } from '../components/templates/SortableList';
import { Icon, GripIcon } from '../components/templates/icons';
import { templatesApi, type TemplateSection, type TemplateSource } from '../api/templates';
import { TemplateSourceBadge } from '../components/templates/TemplateSourceBadge';
import { useToast } from '../ui';

type Confirm =
  | { kind: 'delete-section'; section: TemplateSection }
  | { kind: 'restore-section'; section: TemplateSection }
  | { kind: 'restore-template' }
  | { kind: 'delete-template' }
  | null;

// First-time intro card title — reflects where this specific template came
// from so a Spectora import doesn't falsely claim to be built off the
// Flowspect default, a duplicate doesn't imply a fresh starting point, etc.
// The intro-dismissed flag is per-user (users.template_intro_dismissed),
// so whichever template the inspector opens first frames the card.
const INTRO_TITLES: Record<TemplateSource, string> = {
  default: 'Built from the Flowspect default',
  custom: 'Your custom template',
  duplicated: 'Duplicated from another template',
  spectora_import: 'Imported from Spectora',
};

export default function TemplatesPage() {
  const navigate = useNavigate();
  // `/templates/:id` is the detail view. `/templates` (no id) still renders
  // this page for legacy callers and falls back to the user's default template;
  // the Templates index lives at `/templates` via TemplatesIndexPage.
  const { id: routeId } = useParams<{ id?: string }>();
  const { template, loading, error, saveState, run, reload } = useTemplate(routeId);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [settingDefault, setSettingDefault] = useState(false);
  const toast = useToast();

  const { live, counts } = useMemo(() => {
    if (!template) return { live: [] as TemplateSection[], counts: { sections: 0, components: 0 } };
    // Every row is a regular section now — the legacy SoP appendix is gone
    // (per-section sop_body lives on each section's detail page).
    const live = template.sections;
    const components = live.reduce((n, s) => n + s.components.length, 0);
    return { live, counts: { sections: live.length, components } };
  }, [template]);

  if (loading && !template) {
    return <div className="ts-title-sub" style={{ padding: 24 }}>Loading template…</div>;
  }
  if (error && !template) {
    return (
      <div style={{ padding: 24 }}>
        <p className="ts-title-sub" style={{ color: 'var(--safety-fg)' }}>{error}</p>
        <button type="button" className="ts-btn ts-btn--secondary" onClick={reload}>Retry</button>
      </div>
    );
  }
  if (!template) return null;

  const sectionsAtLeastTwo = live.length > 1;

  const handleRename = (name: string) => { run(() => templatesApi.rename(template.id, name)); };

  const handleCreateSection = () => {
    run(() => templatesApi.createSection(template.id, 'New section'));
  };

  const handleReorder = (ids: string[]) => {
    // Optimistic: paint the new order immediately so the drag doesn't
    // snap back to the old position while the server round-trips.
    run(
      () => templatesApi.reorderSections(template.id, ids),
      (prev) => {
        const byId = new Map(prev.sections.map((s) => [s.id, s]));
        const reordered = ids
          .map((id, i) => {
            const s = byId.get(id);
            return s ? { ...s, orderIndex: i } : null;
          })
          .filter((s): s is NonNullable<typeof s> => s !== null);
        const untouched = prev.sections.filter((s) => !ids.includes(s.id));
        return { ...prev, sections: [...reordered, ...untouched] };
      },
    );
  };

  const handleDismissIntro = () => {
    run(
      () => templatesApi.dismissIntro(),
      (prev) => ({ ...prev, introDismissed: true }),
    );
  };

  const closeConfirm = () => { if (!busy) setConfirm(null); };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'delete-section') {
        await run(() => templatesApi.deleteSection(confirm.section.id));
      } else if (confirm.kind === 'restore-section') {
        await run(() => templatesApi.restoreSection(confirm.section.id));
      } else if (confirm.kind === 'restore-template') {
        await run(() => templatesApi.restoreDefaults(template.id));
      } else if (confirm.kind === 'delete-template') {
        await templatesApi.remove(template.id);
        toast.show({ tone: 'success', title: `Deleted "${template.name}"` });
        navigate('/templates');
        return;
      }
      setConfirm(null);
    } catch (e) {
      if (confirm.kind === 'delete-template') {
        toast.show({ tone: 'critical', title: (e as Error).message ?? 'Failed to delete template' });
        setConfirm(null);
      }
    } finally { setBusy(false); }
  };

  const handleSetDefault = async () => {
    setSettingDefault(true);
    try {
      await run(() => templatesApi.setDefault(template.id));
    } finally { setSettingDefault(false); }
  };

  return (
    <>
      <div style={{ marginBottom: 8 }}>
        <Link to="/templates" className="ts-title-sub" style={{ textDecoration: 'none' }}>
          ← Templates
        </Link>
      </div>
      <div className="ts-title-row">
        <div>
          <h1 className="ts-title">
            <InlineRename
              value={template.name}
              onCommit={handleRename}
              font="display"
              size={38}
              ariaLabel="Rename template"
            />
          </h1>
          <div className="ts-title-sub" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <TemplateSourceBadge source={template.source} />
            {template.isDefault && <span className="ts-customized" style={{ background: 'var(--success-50, #ecfdf5)', color: 'var(--success-700, #047857)' }}>Default</span>}
            <span>
              {counts.sections} sections · {counts.components} components
            </span>
          </div>
        </div>
        <div className="ts-head-actions">
          <AutosaveIndicator state={saveState} onRetry={reload} />
          {!template.isDefault && (
            <button
              type="button"
              className="ts-btn ts-btn--secondary ts-btn--sm"
              onClick={handleSetDefault}
              disabled={settingDefault}
            >
              {settingDefault ? 'Saving…' : 'Set as default'}
            </button>
          )}
          <button
            type="button"
            className="ts-btn ts-btn--destructive-ghost ts-btn--sm"
            onClick={() => setConfirm({ kind: 'restore-template' })}
          >
            Restore defaults
          </button>
          <button
            type="button"
            className="ts-btn ts-btn--danger ts-btn--sm"
            onClick={() => setConfirm({ kind: 'delete-template' })}
          >
            Delete template
          </button>
        </div>
      </div>

      {!template.introDismissed && (
        <div className="ts-intro" role="note">
          <div className="ts-intro-accent" aria-hidden="true" />
          <div className="ts-intro-body">
            <div className="ts-intro-eyebrow">Your template</div>
            <div className="ts-intro-title">{INTRO_TITLES[template.source]}</div>
            <div className="ts-intro-text">
              Edit any section to match your practice &mdash; changes won&rsquo;t
              affect inspections you&rsquo;ve already created.
            </div>
          </div>
          <button
            type="button"
            className="ts-intro-close"
            aria-label="Dismiss intro card"
            onClick={handleDismissIntro}
          >
            <Icon name="x" size={14} />
          </button>
        </div>
      )}

      <div className="ts-card" style={{ marginTop: 8 }}>
        <div className="ts-card-head">
          <span className="ts-card-head-label">Sections</span>
          <span className="ts-card-head-meta">{live.length} · drag to reorder</span>
        </div>
        <SortableList
          groupKey="sections"
          items={live}
          onReorder={handleReorder}
          renderItem={(sec, { dragHandleProps, state }) => (
            <SectionRow
              section={sec}
              dragState={state}
              dragHandleProps={dragHandleProps}
              onOpen={() => navigate(`/templates/${template.id}/sections/${sec.id}`)}
              onDelete={() => setConfirm({ kind: 'delete-section', section: sec })}
              onRestore={() => setConfirm({ kind: 'restore-section', section: sec })}
              canDelete={sectionsAtLeastTwo}
            />
          )}
        />
        <button
          type="button"
          className="ts-attr-add"
          style={{ borderTop: '1px solid var(--border-1)' }}
          onClick={handleCreateSection}
        >
          <Icon name="plus" size={12} /> Add section
        </button>
      </div>

      {/* The Standards of Practice "appendix" link used to live here. SoP
          is now per-section — open any section to edit its Standards body. */}

      {confirm?.kind === 'delete-section' && (
        <ConfirmModal
          title="Delete this section?"
          body={<>Delete <strong>&ldquo;{confirm.section.name}&rdquo;</strong>? This section and its <strong>{confirm.section.components.length} components</strong> will be removed from your template.</>}
          confirmLabel="Delete section"
          busy={busy}
          onCancel={closeConfirm}
          onConfirm={runConfirm}
        />
      )}
      {confirm?.kind === 'restore-section' && (
        <ConfirmModal
          title={`Restore '${confirm.section.name}' to default?`}
          body={<>Restore the <strong>{confirm.section.name}</strong> section to the Flowspect default. Any customizations you&rsquo;ve made to this section will be lost.</>}
          confirmLabel="Restore section"
          busy={busy}
          onCancel={closeConfirm}
          onConfirm={runConfirm}
        />
      )}
      {confirm?.kind === 'restore-template' && (
        <ConfirmModal
          title="Restore entire template to default?"
          body={<>Reset your template to the Flowspect default. All customizations will be lost.</>}
          detail={<ul><li>This doesn&rsquo;t affect inspections you&rsquo;ve already created.</li><li>Only new inspections use the updated template.</li></ul>}
          confirmLabel="Restore template"
          busy={busy}
          onCancel={closeConfirm}
          onConfirm={runConfirm}
        />
      )}
      {confirm?.kind === 'delete-template' && (
        <ConfirmModal
          title={`Delete "${template.name}"?`}
          body={<>This removes the template from your library. Inspections that already use it are unaffected — they keep their own copy.</>}
          confirmLabel="Delete template"
          busy={busy}
          onCancel={closeConfirm}
          onConfirm={runConfirm}
        />
      )}
    </>
  );
}

interface SectionRowProps {
  section: TemplateSection;
  dragState: 'resting' | 'dragging' | 'drop-target' | 'ghost';
  dragHandleProps: { draggable: true; onDragStart: (e: React.DragEvent) => void; onDragEnd: () => void };
  onOpen: () => void;
  onDelete: () => void;
  onRestore: () => void;
  canDelete: boolean;
}

function SectionRow({
  section,
  dragState,
  dragHandleProps,
  onOpen,
  onDelete,
  onRestore,
  canDelete,
}: SectionRowProps) {
  const cls = ['ts-row', 'ts-row--section'];
  if (dragState === 'dragging') cls.push('ts-row--dragging');
  if (dragState === 'drop-target') cls.push('ts-row--drop-target');

  return (
    <div className={cls.join(' ')} onClick={onOpen} role="button" tabIndex={0}>
      <span className="ts-grip" aria-label="Drag to reorder" {...dragHandleProps}>
        <GripIcon size={14} />
      </span>
      <div className="ts-sec-name">
        <span className="ts-sec-name-text">{section.name}</span>
        {section.isCustomized && <span className="ts-customized">Customized</span>}
      </div>
      <span className="ts-sec-meta">{section.components.length} components</span>
      <span className="ts-chev"><Icon name="chev" size={16} /></span>
      <div onClick={(e) => e.stopPropagation()}>
        <RowMenu
          items={[
            { label: 'Restore default', icon: 'reset', onClick: onRestore },
            'separator',
            { label: canDelete ? 'Delete' : 'Delete (last section)', icon: 'trash', danger: true, onClick: onDelete, disabled: !canDelete },
          ]}
        />
      </div>
    </div>
  );
}
